<template>
  <div v-if="groups.length > 0 || isLive" class="flex flex-col gap-1.5">
    <div v-if="isLive" class="flex items-center gap-2 px-1">
      <span class="cleo-status-orb shrink-0" />
      <span class="cleo-shimmer-text flex-1 min-w-0 truncate text-xs font-medium">{{ statusText }}</span>
      <span class="shrink-0 text-[10px] font-medium tabular-nums" style="color: #a4adba;">
        {{ doneCalls }}/{{ totalCalls }}<template v-if="elapsedLabel"> · {{ elapsedLabel }}</template>
      </span>
    </div>

    <TransitionGroup name="cleo-step" tag="div" class="flex flex-col">
      <div v-for="(g, gi) in groups" :key="g.id" class="flex gap-2">
        <div class="relative w-[10px] shrink-0 self-stretch" aria-hidden="true">
          <span v-if="gi > 0" class="cleo-rail-line" :style="`top: 0; height: ${railTop(g)}px;`" />
          <span v-if="gi < groups.length - 1" class="cleo-rail-line" :style="`top: ${railTop(g)}px; bottom: 0;`" />
          <span v-if="gi === 0" class="cleo-rail-dot" :style="`top: ${railTop(g)}px;`" :class="isLive && g.status === 'pending' ? 'cleo-rail-dot-live' : ''" />
          <span
            v-else
            class="cleo-rail-node"
            :style="`top: ${railTop(g)}px;`"
            :class="g.kind === 'thinking' ? 'cleo-rail-node-thinking' : (g.status === 'pending' && isLive ? 'cleo-rail-node-active' : (g.status === 'error' ? 'cleo-rail-node-error' : ''))"
          />
        </div>

        <p
          v-if="g.kind === 'thinking'"
          class="cleo-thinking-note flex-1 min-w-0 text-[11px] leading-relaxed"
          :class="gi < groups.length - 1 ? 'mb-2' : ''"
          style="color: #475569; white-space: pre-wrap;"
        >{{ g.result }}</p>

        <div
        v-else
        class="cleo-tool-card flex-1 min-w-0"
        :class="[
          g.status === 'pending' && isLive ? 'cleo-tool-card-live' : '',
          gi < groups.length - 1 ? 'mb-1.5' : '',
        ]"
      >
        <button
          class="w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-colors focus:outline-none"
          :class="hasBody(g) ? 'hover:bg-slate-50/70' : 'cursor-default'"
          @click="hasBody(g) && toggleCard(g.id)"
        >
          <span
            class="shrink-0 w-[24px] h-[24px] flex items-center justify-center rounded-md"
            :style="{ background: metaFor(g).bg, color: metaFor(g).fg }"
          >
            <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path :d="metaFor(g).icon"/>
            </svg>
          </span>

          <span class="flex-1 min-w-0">
            <span class="block truncate text-xs font-semibold leading-tight" style="color: #334155;">{{ metaFor(g).title }}</span>
            <span
              class="block truncate text-[10px] leading-snug mt-px"
              :class="g.status === 'pending' && isLive ? 'cleo-shimmer-text font-medium' : ''"
              :style="g.status === 'pending' && isLive ? '' : 'color:#94a3b8;'"
            >{{ groupSubtitle(g) }}</span>
          </span>

          <span v-if="g.status === 'done'" class="cleo-check-pop shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style="background:#e5f4ec;">
            <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#3e8e63" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
          </span>
          <span v-else-if="g.status === 'error'" class="shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style="background:#f9ecec;">
            <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#b06060" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </span>
          <span v-else-if="!isLive" class="shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style="background:#f1f5f9;">
            <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" stroke-width="3"><path stroke-linecap="round" d="M6 12h12"/></svg>
          </span>
          <svg v-else class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" style="color:#9db0c9;">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>

          <svg
            v-if="hasBody(g)"
            class="w-3 h-3 shrink-0 transition-transform duration-200"
            :class="isCardOpen(g) ? 'rotate-90' : ''"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" style="color: #c2c9d6;"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div v-if="isCardOpen(g)" style="border-top: 1px solid rgba(15,23,42,0.05);">
          <TransitionGroup v-if="g.children.length > 0" name="cleo-step" tag="div" class="flex flex-col px-2.5 py-1">
            <div v-for="(c, ci) in g.children" :key="c.id">
              <div class="flex items-center gap-2 py-[5px]" :style="ci > 0 ? 'border-top: 1px solid rgba(15,23,42,0.04);' : ''">
                <span v-if="c.status === 'done'" class="cleo-check-pop shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center" style="background:#e5f4ec;">
                  <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#3e8e63" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                </span>
                <span v-else-if="c.status === 'error'" class="shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center" style="background:#f9ecec;">
                  <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#b06060" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </span>
                <span v-else-if="!isLive" class="shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center" style="background:#f1f5f9;">
                  <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" stroke-width="3"><path stroke-linecap="round" d="M6 12h12"/></svg>
                </span>
                <svg v-else class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" style="color:#9db0c9;">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>

                <span
                  class="flex-1 min-w-0 truncate text-[11px] font-medium leading-tight"
                  :class="c.status === 'pending' && isLive ? 'cleo-shimmer-text' : ''"
                  :style="c.status === 'pending' && isLive ? '' : (c.status === 'error' ? 'color:#b06060;' : 'color:#475569;')"
                >{{ childLabel(c.fn) }}</span>
              </div>
            </div>
          </TransitionGroup>
        </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';

export interface ToolPanelEvent {
  event: string;
  plugin: string;
  fn: string;
  args?: Record<string, string> | null;
  result?: string | null;
  error?: string | null;
}

interface CallInfo {
  id: string;
  plugin: string;
  fn: string;
  args?: Record<string, string>;
  result?: string;
  error?: string;
  status: 'pending' | 'done' | 'error';
}

interface StepGroup extends CallInfo {
  kind: 'planning' | 'agent' | 'nav' | 'title' | 'tool' | 'thinking';
  children: CallInfo[];
}

const props = defineProps<{
  events: ToolPanelEvent[];
  isLive?: boolean;
}>();

const expandedCards = ref<Set<string>>(new Set());

const toggleCard = (id: string): void => {
  if (expandedCards.value.has(id)) {
    expandedCards.value.delete(id);
  } else {
    expandedCards.value.add(id);
  }
};

// ── Elapsed timer (only ticks for turns observed live) ──────────────────────
const startedAt = ref<number | null>(null);
const nowTick = ref(Date.now());
const frozenElapsedMs = ref<number | null>(null);
let tickTimer: ReturnType<typeof setInterval> | null = null;

watch(() => props.isLive, (live) => {
  if (live && startedAt.value === null) {
    startedAt.value = Date.now();
    tickTimer = setInterval(() => { nowTick.value = Date.now(); }, 250);
  }
  if (!live && tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
    if (startedAt.value !== null) frozenElapsedMs.value = Date.now() - startedAt.value;
  }
}, { immediate: true });

onUnmounted(() => { if (tickTimer) clearInterval(tickTimer); });

const elapsedMs = computed((): number => {
  if (startedAt.value === null) return 0;
  return (props.isLive ? nowTick.value : (startedAt.value + (frozenElapsedMs.value ?? 0))) - startedAt.value;
});

const elapsedLabel = computed((): string => {
  if (startedAt.value === null) return '';
  return `${Math.floor(elapsedMs.value / 1000)}s`;
});

// ── Icons (heroicons outline, merged path data) ─────────────────────────────
const ICONS: Record<string, string> = {
  map: 'M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z',
  building: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z',
  truck: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
  globe: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
  cube: 'm21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9',
  banknotes: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
  pin: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  link: 'M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244',
  tag: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z M6 6h.008v.008H6V6z',
  spark: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z',
  document: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
};

// ── Domain color pairs ───────────────────────────────────────────────────────
const COLORS = {
  violet:  { bg: '#f3e8ff', fg: '#7c3aed' },
  blue:    { bg: '#e8f0fe', fg: '#004AAD' },
  orange:  { bg: '#ffedd5', fg: '#ea580c' },
  cyan:    { bg: '#cffafe', fg: '#0e7490' },
  amber:   { bg: '#fef3c7', fg: '#b45309' },
  emerald: { bg: '#d1fae5', fg: '#047857' },
  slate:   { bg: '#f1f5f9', fg: '#475569' },
};

interface GroupMeta { title: string; icon: string; bg: string; fg: string; live: string }

const AGENT_META: Record<string, GroupMeta> = {
  RunCompanyProfileAgent:     { title: 'Company profile specialist',    icon: ICONS.building,  ...COLORS.blue,    live: 'Checking your company profile…' },
  RunDocumentationAgent:      { title: 'Portal guide specialist',       icon: ICONS.document,  ...COLORS.violet,  live: 'Looking it up in the guide…' },
  RunTransportRoadAgent:      { title: 'Road transport specialist',     icon: ICONS.truck,     ...COLORS.orange,  live: 'Digging through your road shipments…' },
  RunSeaAirAgent:             { title: 'Sea & Air specialist',          icon: ICONS.globe,     ...COLORS.cyan,    live: 'Scanning your sea & air shipments…' },
  RunLogisticsAgent:          { title: 'Warehousing specialist',        icon: ICONS.cube,      ...COLORS.amber,   live: 'Checking your warehouse records…' },
  RunFinanceAgent:            { title: 'Finance specialist',            icon: ICONS.banknotes, ...COLORS.emerald, live: 'Crunching the numbers…' },
  RunShipmentActivitiesAgent: { title: 'Shipment tracking specialist',  icon: ICONS.pin,       ...COLORS.orange,  live: 'Tracing shipment activity…' },
};

// Disambiguates child attribution when several specialists run in parallel.
const FUNCTION_AGENT: Record<string, string> = {
  GetMyBusinessLines: 'RunCompanyProfileAgent', GetMyCompanyProfile: 'RunCompanyProfileAgent',
  GetDomesticOrderEntrySettings: 'RunCompanyProfileAgent', GetInternationalOrderEntrySettings: 'RunCompanyProfileAgent',
  GetMyOrderEntryAddresses: 'RunCompanyProfileAgent', GetMyLeadtimes: 'RunCompanyProfileAgent',
  GetDataSyncStatus: 'RunCompanyProfileAgent',
  SearchDocumentation: 'RunDocumentationAgent',
  SearchDomesticShipments: 'RunTransportRoadAgent', SearchInternationalShipments: 'RunTransportRoadAgent', SearchStrongoShipments: 'RunTransportRoadAgent',
  SearchSeaShipments: 'RunSeaAirAgent', SearchAirShipments: 'RunSeaAirAgent',
  SearchInbounds: 'RunLogisticsAgent', SearchOutbounds: 'RunLogisticsAgent', SearchVbsInbounds: 'RunLogisticsAgent', SearchVbsOutbounds: 'RunLogisticsAgent',
  SearchCustomerLedgerEntries: 'RunFinanceAgent', GetCustomerLedgerEntryWithGlEntries: 'RunFinanceAgent',
  SearchCustomerLedgerGlEntries: 'RunFinanceAgent',
  GetCreditAndUnpaidSummary: 'RunFinanceAgent', SearchInvoices: 'RunFinanceAgent',
  GetErpInvoiceLineNames: 'RunFinanceAgent', GetGlTransportTypes: 'RunFinanceAgent', RangeQuery: 'RunFinanceAgent',
  SearchDomesticRoadActivities: 'RunShipmentActivitiesAgent', SearchInternationalRoadActivities: 'RunShipmentActivitiesAgent', SearchStrongoActivities: 'RunShipmentActivitiesAgent',
};

const CHILD_LABEL: Record<string, string> = {
  GetMyBusinessLines: 'Your active services', GetMyCompanyProfile: 'Company profile',
  GetDomesticOrderEntrySettings: 'Domestic order settings', GetInternationalOrderEntrySettings: 'International order settings',
  GetMyOrderEntryAddresses: 'Order entry addresses', GetMyLeadtimes: 'Lead times',
  GetDataSyncStatus: 'Data sync status',
  SearchDocumentation: 'Portal guide',
  SearchDomesticShipments: 'Domestic shipments', SearchInternationalShipments: 'International shipments', SearchStrongoShipments: 'Strongo shipments',
  SearchSeaShipments: 'Sea shipments', SearchAirShipments: 'Air shipments',
  SearchInbounds: 'Inbound orders', SearchOutbounds: 'Outbound orders', SearchVbsInbounds: 'VBS inbounds', SearchVbsOutbounds: 'VBS outbounds',
  SearchCustomerLedgerEntries: 'Invoice ledger', GetCustomerLedgerEntryWithGlEntries: 'Invoice breakdown',
  SearchCustomerLedgerGlEntries: 'Cost per service',
  GetCreditAndUnpaidSummary: 'Credit & unpaid summary', SearchInvoices: 'Invoices',
  GetErpInvoiceLineNames: 'Invoice line names', GetGlTransportTypes: 'Transport types', RangeQuery: 'Range query',
  SearchDomesticRoadActivities: 'Domestic route activity', SearchInternationalRoadActivities: 'International route activity', SearchStrongoActivities: 'Strongo tracking',
  GetCurrentDateTime: 'Current date & time',
};

const ACTION_TEXT: Record<string, string> = {
  ConsultAllPlanners:                  'Mapping out the best route to your answer…',
  RunCompanyProfileAgent:              'Checking your company profile…',
  RunDocumentationAgent:               'Looking it up in the guide…',
  RunTransportRoadAgent:               'Digging through your road shipments…',
  RunSeaAirAgent:                      'Scanning your sea & air shipments…',
  RunLogisticsAgent:                   'Checking your warehouse records…',
  RunFinanceAgent:                     'Crunching the numbers…',
  RunShipmentActivitiesAgent:          'Tracing shipment activity…',
  OfferDestinations:                   'Preparing quick links…',
  SetTitle:                            'Naming this conversation…',
  GetMyBusinessLines:                  'Checking your active services…',
  GetMyCompanyProfile:                 'Loading your company profile…',
  GetDomesticOrderEntrySettings:       'Loading domestic order settings…',
  GetInternationalOrderEntrySettings:  'Loading international order settings…',
  GetMyOrderEntryAddresses:            'Loading your saved addresses…',
  GetMyLeadtimes:                      'Loading your lead times…',
  GetDataSyncStatus:                   'Checking when your data was last synced…',
  SearchDocumentation:                 'Searching the portal guide…',
  SearchDomesticShipments:             'Searching domestic shipments…',
  SearchInternationalShipments:        'Searching international shipments…',
  SearchStrongoShipments:              'Searching Strongo shipments…',
  SearchSeaShipments:                  'Searching sea shipments…',
  SearchAirShipments:                  'Searching air shipments…',
  SearchInbounds:                      'Searching inbound orders…',
  SearchOutbounds:                     'Searching outbound orders…',
  SearchVbsInbounds:                   'Searching VBS inbounds…',
  SearchVbsOutbounds:                  'Searching VBS outbounds…',
  SearchCustomerLedgerEntries:         'Retrieving invoices…',
  GetCustomerLedgerEntryWithGlEntries: 'Loading invoice details…',
  SearchCustomerLedgerGlEntries: 'Breaking down your costs per service…',
  GetCreditAndUnpaidSummary:           'Summarising credit & unpaid amounts…',
  SearchInvoices:                      'Searching invoices…',
  GetErpInvoiceLineNames:              'Decoding invoice lines…',
  GetGlTransportTypes:                 'Mapping transport types…',
  RangeQuery:                          'Running a range query…',
  SearchDomesticRoadActivities:        'Tracing domestic route activity…',
  SearchInternationalRoadActivities:   'Tracing international route activity…',
  SearchStrongoActivities:             'Tracing Strongo parcels…',
  GetCurrentDateTime:                  'Checking the calendar…',
};

const formatFnName = (fn: string): string => fn.replace(/([A-Z])/g, ' $1').trim();

// ── Grouping: orchestrator steps become the cards, data calls nest under their specialist ──
const groups = computed<StepGroup[]>(() => {
  const gs: StepGroup[] = [];
  let seq = 0;

  const findPendingAgent = (fn: string): StepGroup | undefined => {
    const pendingAgents = gs.filter(g => g.kind === 'agent' && g.status === 'pending');
    if (pendingAgents.length === 0) return undefined;
    if (pendingAgents.length === 1) return pendingAgents[0];
    const owner = FUNCTION_AGENT[fn];
    return pendingAgents.find(g => g.fn === owner) ?? pendingAgents[pendingAgents.length - 1];
  };

  for (const ev of props.events) {
    if (ev.event === 'thinking') {
      const text = (ev.result ?? '').trim();
      if (text.length > 0) {
        gs.push({
          id: `s${seq++}`,
          plugin: ev.plugin,
          fn: ev.fn,
          result: text,
          status: 'done',
          kind: 'thinking',
          children: [],
        });
      }
    } else if (ev.event === 'tool_call') {
      const base: CallInfo = {
        id: `s${seq++}`,
        plugin: ev.plugin,
        fn: ev.fn,
        args: ev.args ?? undefined,
        status: 'pending',
      };
      if (ev.plugin === 'Execution') {
        gs.push({ ...base, kind: 'agent', children: [] });
      } else if (ev.plugin === 'Planners') {
        gs.push({ ...base, kind: 'planning', children: [] });
      } else if (ev.plugin === 'Navigation') {
        gs.push({ ...base, kind: 'nav', children: [] });
      } else if (ev.plugin === 'Title') {
        gs.push({ ...base, kind: 'title', children: [] });
      } else {
        const parent = findPendingAgent(ev.fn);
        if (parent) {
          parent.children.push(base);
        } else {
          gs.push({ ...base, kind: 'tool', children: [] });
        }
      }
    } else if (ev.event === 'tool_result' || ev.event === 'tool_error') {
      const all: CallInfo[] = [];
      for (const g of gs) {
        all.push(...g.children);
        all.push(g);
      }
      const match = [...all].reverse().find(c => c.plugin === ev.plugin && c.fn === ev.fn && c.status === 'pending');
      if (match) {
        if (ev.event === 'tool_result') {
          match.result = ev.result ?? undefined;
          match.status = 'done';
        } else {
          match.error = ev.error ?? undefined;
          match.status = 'error';
        }
      }
    }
  }
  return gs;
});

const allCalls = computed<CallInfo[]>(() => {
  const out: CallInfo[] = [];
  for (const g of groups.value) {
    if (g.kind === 'thinking') continue;
    out.push(g);
    out.push(...g.children);
  }
  return out;
});

const totalCalls = computed(() => allCalls.value.length);
const doneCalls = computed(() => allCalls.value.filter(c => c.status !== 'pending').length);

// ── Thinking (orchestrator narration) — inline notes at their chronological spot ─
const railTop = (g: StepGroup): number => (g.kind === 'thinking' ? 10 : 21);

// ── Header status line ───────────────────────────────────────────────────────
const GENERIC_PHRASES = [
  'Reading your message…',
  'Thinking it through…',
  'Rounding up the right specialists…',
  'Checking your live OneClick data…',
];

const statusText = computed((): string => {
  if (props.events.length === 0) {
    const idx = Math.floor(elapsedMs.value / 2400) % GENERIC_PHRASES.length;
    return GENERIC_PHRASES[idx];
  }
  for (const g of [...groups.value].reverse()) {
    if (g.status !== 'pending') continue;
    const pendingChild = [...g.children].reverse().find(c => c.status === 'pending');
    if (pendingChild) {
      return ACTION_TEXT[pendingChild.fn] ?? `Checking ${childLabel(pendingChild.fn).toLowerCase()}…`;
    }
    return ACTION_TEXT[g.fn] ?? 'Working on it…';
  }
  return 'Putting it all together…';
});

// ── Row display helpers ──────────────────────────────────────────────────────
const metaFor = (g: StepGroup): GroupMeta => {
  if (g.kind === 'planning') return { title: 'Planning the approach', icon: ICONS.map, ...COLORS.violet, live: ACTION_TEXT.ConsultAllPlanners };
  if (g.kind === 'nav')      return { title: 'Quick links',           icon: ICONS.link, ...COLORS.blue,  live: ACTION_TEXT.OfferDestinations };
  if (g.kind === 'title')    return { title: 'Conversation title',    icon: ICONS.tag,  ...COLORS.amber, live: ACTION_TEXT.SetTitle };
  if (g.kind === 'agent') {
    return AGENT_META[g.fn] ?? {
      title: `${formatFnName(g.fn).replace(/^Run /, '').replace(/ Agent$/, '')} specialist`,
      icon: ICONS.spark, ...COLORS.blue,
      live: ACTION_TEXT[g.fn] ?? 'Working on it…',
    };
  }
  return { title: childLabel(g.fn), icon: ICONS.spark, ...COLORS.slate, live: ACTION_TEXT[g.fn] ?? 'Working on it…' };
};

const groupSubtitle = (g: StepGroup): string => {
  if (g.status === 'pending') return props.isLive ? metaFor(g).live : 'Not completed';
  if (g.status === 'error') return 'Something went wrong here';
  if (g.kind === 'planning') return 'Route planned';
  if (g.kind === 'nav') return 'Quick links ready';
  if (g.kind === 'title') return 'Conversation named';
  if (g.kind === 'agent') {
    const n = g.children.length;
    if (n === 0) return 'Completed';
    return `${n} data check${n === 1 ? '' : 's'} completed`;
  }
  return 'Completed';
};

const childLabel = (fn: string): string => CHILD_LABEL[fn] ?? formatFnName(fn);

const hasBody = (g: StepGroup): boolean => g.kind === 'agent' && g.children.length > 0;

const isCardOpen = (g: StepGroup): boolean =>
  expandedCards.value.has(g.id) || (!!props.isLive && g.kind === 'agent' && g.status === 'pending' && g.children.length > 0);
</script>

<style scoped>
/* ── Tool card (Claude-Desktop-style) ── */
.cleo-tool-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  overflow: hidden;
}
.cleo-tool-card-live {
  animation: cleo-card-live 2.4s ease-in-out infinite;
}
@keyframes cleo-card-live {
  0%, 100% { border-color: rgba(0, 74, 173, 0.1); }
  50%      { border-color: rgba(0, 74, 173, 0.28); }
}

/* ── Thinking notes (orchestrator narration, inline in the timeline) ── */
.cleo-thinking-note {
  border-left: 2px solid rgba(124, 58, 237, 0.28);
  padding-left: 8px;
  padding-top: 1px;
  padding-bottom: 1px;
}
.cleo-rail-node-thinking {
  background: #c4b5fd;
}

/* ── Timeline rail (connects the cards) ── */
.cleo-rail-line {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 1.5px;
  background: rgba(0, 74, 173, 0.14);
  border-radius: 1px;
}
.cleo-rail-dot {
  position: absolute;
  left: 50%;
  top: 21px;
  transform: translate(-50%, -50%);
  width: 9px;
  height: 9px;
  border-radius: 9999px;
  background: #ffffff;
  border: 2px solid #7f9cc7;
}
.cleo-rail-node {
  position: absolute;
  left: 50%;
  top: 21px;
  transform: translate(-50%, -50%);
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background: #a9bdd9;
  box-shadow: 0 0 0 1.5px #ffffff;
}
.cleo-rail-node-active {
  animation: cleo-rail-node-fill 1.6s ease-in-out infinite;
}
@keyframes cleo-rail-node-fill {
  0%, 100% { background: #dde7f3; transform: translate(-50%, -50%) scale(0.8); }
  50%      { background: #7f9cc7; transform: translate(-50%, -50%) scale(1.3); }
}
.cleo-rail-node-error {
  background: #cf9a9a;
}
.cleo-rail-dot-live {
  animation: cleo-rail-dot-pulse 1.9s ease-in-out infinite;
}
@keyframes cleo-rail-dot-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 74, 173, 0.2); }
  50%      { box-shadow: 0 0 0 4px rgba(0, 74, 173, 0); }
}

/* ── Status orb (live line) ── */
.cleo-status-orb {
  width: 9px;
  height: 9px;
  border-radius: 9999px;
  background: radial-gradient(circle at 32% 28%, #c9d8ee, #7f9cc7 55%, #004aad 160%);
  animation: cleo-orb-pulse 1.9s ease-in-out infinite;
}
@keyframes cleo-orb-pulse {
  0%, 100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(0, 74, 173, 0.2); }
  50%      { transform: scale(1.12); box-shadow: 0 0 0 5px rgba(0, 74, 173, 0); }
}

/* ── Shimmer text (thinking shimmer) ── */
.cleo-shimmer-text {
  background: linear-gradient(90deg, #9aa5b6 0%, #5a769e 42%, #46648f 50%, #5a769e 58%, #9aa5b6 100%);
  background-size: 220% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: cleo-shimmer 2.6s linear infinite;
}
@keyframes cleo-shimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}

/* ── Done check pop-in ── */
.cleo-check-pop {
  animation: cleo-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes cleo-pop {
  0%   { transform: scale(0); }
  70%  { transform: scale(1.25); }
  100% { transform: scale(1); }
}

/* ── Cards & rows slide in ── */
.cleo-step-enter-active {
  transition: opacity 0.32s cubic-bezier(0.22, 1, 0.36, 1), transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}
.cleo-step-enter-from {
  opacity: 0;
  transform: translateY(7px);
}
</style>
